import { useContext, useState } from 'react';

import {
  formatADOTConversion,
  getADOTReplenishmentTotals,
  isADOTBaseUnit,
} from '@atom/components/common/requests/adotReplenishmentUtilities';
import { canCreateADOTRequest } from '@atom/components/common/requests/adotRequestPermissions';
import { TYPE_LABELS } from '@atom/components/common/requests/assetRequestConstants';
import { Button, Icon } from '@atom/mui';
import { AddIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetRequestStatus } from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ViewRequestContext from '../ViewRequestContext';

import ADOTAddReplenishmentView from './ADOTAddReplenishmentView';
import ADOTConvertedQuantity from './ADOTConvertedQuantity';
import ADOTEditReplenishmentView from './ADOTEditReplenishmentView';
import ADOTReplenishmentCard from './ADOTReplenishmentCard';

import classes from './adotView.module.css';

const ADOTReplenishmentView = () => {
  const { activeRequest } = useContext(ViewRequestContext);

  const [createView, setCreateView] = useState<boolean>(false);
  const [editId, setEditId] = useState<string>();

  const { purchaseUnit, orderedBase, receivedBase, remainingBase } =
    getADOTReplenishmentTotals(activeRequest);

  const showCreateButton =
    canCreateADOTRequest() &&
    activeRequest?.status === AssetRequestStatus.PARTIALLY_RECEIVED &&
    isNilOrEmpty(editId);

  return (
    <div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Request Type</div>
        <div>{TYPE_LABELS[activeRequest?.type]}</div>
      </div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Inventory Name</div>
        <div>{activeRequest?.assetName}</div>
      </div>
      <div className={classes.viewRow}>
        <div className={classes.rowTitle}>Purchase Order Number</div>
        <div data-cy="adotReplenishmentPurchaseOrderNumber">
          {activeRequest?.purchaseOrderNumber || '-'}
        </div>
      </div>
      <div className={classes.replenishmentRow}>
        <div>
          <div className={classes.rowTitle}>Total Qty Ordered</div>
          <ADOTConvertedQuantity
            baseValue={orderedBase}
            purchaseUnit={purchaseUnit}
            dataCy="adotReplenishmentTotalOrdered"
          />
        </div>
        <div>
          <div className={classes.rowTitle}>Purchase Unit</div>
          <div data-cy="adotReplenishmentPurchaseUnit">
            <div>{purchaseUnit.unit}</div>
            {!isADOTBaseUnit(purchaseUnit) && (
              <div className={classes.subtext}>
                {formatADOTConversion(purchaseUnit)}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className={classes.rowTitle}>Total Qty Received</div>
          <ADOTConvertedQuantity
            baseValue={receivedBase}
            purchaseUnit={purchaseUnit}
            dataCy="adotReplenishmentTotalReceived"
          />
        </div>
        <div>
          <div className={classes.rowTitle}>Total Remaining Qty on Order</div>
          <ADOTConvertedQuantity
            baseValue={remainingBase}
            purchaseUnit={purchaseUnit}
            dataCy="adotReplenishmentTotalRemaining"
          />
        </div>
      </div>
      <div className={classes.viewRow}>
        {(activeRequest?.replenishments ?? []).map(replenishment =>
          replenishment?.id === editId ? (
            <ADOTEditReplenishmentView
              key={replenishment.id}
              replenishment={replenishment}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <ADOTReplenishmentCard
              key={replenishment.id}
              replenishment={replenishment}
              purchaseUnit={purchaseUnit}
              editId={editId}
              setEditId={setEditId}
            />
          ),
        )}
      </div>
      {createView ? (
        <ADOTAddReplenishmentView onCancel={() => setCreateView(false)} />
      ) : (
        showCreateButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon color={colors.neutral.white} icon={AddIcon} />}
            onClick={() => setCreateView(true)}
            data-cy="adotReplenishmentCreateButton"
          >
            Create Replenishment
          </Button>
        )
      )}
    </div>
  );
};

export default ADOTReplenishmentView;
